import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes, { object } from 'prop-types'
import { Card } from 'react-bootstrap'
import classNames from 'classnames'
import { escapeRegExp, throttle } from 'lodash'

import Searchbar from 'components/Shared/Searchbar'
import { SecondaryButton } from 'components/Shared/Button'
import { useTranslation } from 'react-i18next'
import { RemoveIcon } from 'images'
import useOnClickOutside from 'hooks/UseOnClickOutside'
import LoadingSpinner from 'components/Shared/LoadingSpinner'
import Tabs from 'components/Shared/Tabs'
import { useMediaQuery } from 'hooks/useMediaQuery'

const Row = ({ item, onChange, handleRemoveSelectedItem, isSelected, inputType = 'checkbox', name }) => {
  const [isChecked, setIsChecked] = useState(isSelected)

  useEffect(() => {
    setIsChecked(isSelected)
  }, [isSelected])

  const handleCheck = (e) => {
    if (inputType === 'radio') {
      onChange(item)
    } else {
      if (isChecked) {
        handleRemoveSelectedItem(e, item)
      } else {
        onChange(item)
      }
      setIsChecked(!isChecked)
    }
  }

  const inputId = `audience-item-${item.id}-${item.name}`

  return (
    <label htmlFor={inputId} key={item.id} className="search-dropdown_dropdown_results_item">
      <input
        type={inputType}
        id={inputId}
        name={name}
        onChange={handleCheck}
        checked={isChecked}
      />
      <div className="search-dropdown_dropdown_results_item_label">
        <span>{item.name}</span>
        {<span className="search-dropdown_dropdown_results_item_sub">ID: {item.id}</span>}
      </div>
    </label>
  )
}

Row.propTypes = {
  item: PropTypes.object,
  onChange: PropTypes.func,
  handleRemoveSelectedItem: PropTypes.func,
  isSelected: PropTypes.bool,
  inputType: PropTypes.oneOf(['checkbox', 'radio']),
  name: PropTypes.string,
}

const TryAgain = ({ error, loading, onClick, t }) => {
  return (
    <div className="search-dropdown_try-again">
      {loading ? (
        <LoadingSpinner title={t('Recommendations.campaign.metaFetchLoading')} />
      ) : (
        <span className="error">{t(error)}</span>
      )}
      <SecondaryButton
        onClick={(e) => {
          e.preventDefault()
          onClick()
        }}
        size="small"
        text={t('common.tryAgain')}
        color="navy"
        disabled={loading}
        fullWidth={false}
      />
    </div>
  )
}

TryAgain.propTypes = {
  error: PropTypes.string,
  loading: PropTypes.bool,
  t: PropTypes.func,
  onClick: PropTypes.func,
}

const SelectedAudiences = ({ defaultValue, audiences, handleRemoveSelectedItem, disabled, isDropdownOpen }) => {
  const ref = useRef(null)
  const classes = ref.current
    ? ref.current.offsetHeight > 40
      ? 'align-self-start mt-2'
      : 'align-self-center'
    : 'align-self-center'

  return (
    <div ref={ref} className="d-flex justify-content-between gap-2">
      {audiences.length ? (
        <div className="search-dropdown_multiselect">
          {audiences.map((audience) => {
            if (audience?.name)
              return (
                <div key={`${audience?.name}-${audience?.id}`} className="search-dropdown_multiselect_title">
                  <span>{audience?.name}</span>
                  {!disabled && (
                    <span className="remove">
                      <RemoveIcon width={14} onClick={(e) => handleRemoveSelectedItem(e, audience)} />
                    </span>
                  )}
                </div>
              )
            return null
          })}
        </div>
      ) : (
        defaultValue
      )}
      {!disabled && (
        <div className={`${classes}`}>
          <div className={`accordion-arrow ${isDropdownOpen ? 'open' : ''}`} />
        </div>
      )}
    </div>
  )
}

SelectedAudiences.propTypes = {
  defaultValue: PropTypes.string,
  audiences: PropTypes.arrayOf(object),
  handleRemoveSelectedItem: PropTypes.func,
  disabled: PropTypes.bool,
  isDropdownOpen: PropTypes.bool,
}

const AudienceList = (props) => {
  const { 
    options = [],
    placeholder = 'Search', 
    loading, 
    error, 
    onErrorBtnClick,
    handleSelect, 
    handleRemoveSelectedItem, 
    selectedAudiences = [],
    inputType = 'checkbox',
    radioName,
  } = props

  const [searchTerm, setSearchTerm] = useState('')

  const { t } = useTranslation()

  let filteredItems = useMemo(
    () =>
      options.filter((option) =>
        searchTerm ? option.name.toLowerCase().search(escapeRegExp(searchTerm?.toLowerCase())) > -1 : true,
      ),
    [options, searchTerm],
  )

  const showResults = filteredItems.length || error || loading

  return (
    <div>
      <div className="search-dropdown_dropdown_searchbar">
        <Searchbar
          handleChange={setSearchTerm}
          placeholder={placeholder}
          ariaLabel={placeholder}
          postfixView={<i className="icon fa-solid fa-search" style={{ color: '#ccc' }}></i>}
          defaultValue={searchTerm}
        />
      </div>
              
      <div className="search-dropdown_dropdown_results">
        {(loading || error) && <TryAgain loading={loading} error={error} t={t} onClick={onErrorBtnClick} />}
        
        {showResults ? (
          filteredItems.map((item) => {
            return (
              <Row
                key={item.id}
                item={item}
                onChange={handleSelect}
                handleRemoveSelectedItem={handleRemoveSelectedItem}
                isSelected={selectedAudiences.some((audience) => audience?.id === item?.id)}
                inputType={inputType}
                name={radioName}
              />
            )
          })
        ) : (
          <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
            <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>
              {t('common.noSearchResults')}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

AudienceList.propTypes = {
  options: PropTypes.arrayOf(object),
}

const SearchDropdownMultiSelect = ({
  defaultValue = '',
  value = null,
  onChange = () => {},
  options = [],
  savedAudiences = [],
  disabled = false,
  onErrorBtnClick = () => {},
  loading = false,
  error = null,
  dropdownClasses = '',
}) => {
  const dropdownRef = useRef(null)
  const accordionRef = useRef(null)
  const dropdownListRef = useRef(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const { t } = useTranslation()
  const [selectedAudiences, setSelectedAudiences] = useState([])
  const [activeTab, setActiveTab] = useState(0)

  const isMobile = useMediaQuery('(max-width: 640px)')

  useEffect(() => {
    const isSavedAudienceChoosen = selectedAudiences.some((audience) => savedAudiences.some((saved) => saved.id === audience.id))
    if (isSavedAudienceChoosen) {
      setActiveTab(1)
    } else {
      setActiveTab(0)
    }
  }, [selectedAudiences])

  useEffect(() => {
    if (value && (options.length || savedAudiences.length)) {
      let audienceArray = []
      let audienceIds = value ? value.split(',') : []
      audienceArray = audienceIds.reduce((acc, audienceId) => {
        const foundOption = options.find((option) => option.id === audienceId)
        const foundSavedAudience = savedAudiences.find((audience) => audience.id === audienceId)
        if (foundOption) {
          acc.push(foundOption)
        } else if (foundSavedAudience) {
          acc.push(foundSavedAudience)
        }
        return acc
      }, [])

      if (audienceArray.length > 0) {
        const currentIds = selectedAudiences.map(a => a.id).sort().join(',')
        const newIds = audienceArray.map(a => a.id).sort().join(',')
        if (currentIds !== newIds) {
          setSelectedAudiences(audienceArray)
        }
      }
    } else if (!value && selectedAudiences.length > 0) {
      setSelectedAudiences([])
    }
  }, [value, options, savedAudiences, selectedAudiences])

  useEffect(() => {
    const sideDrawerComponent = document.getElementById('side-drawer-content')

    const handleClick = () => {
      const expectedHeight =
        sideDrawerComponent.offsetHeight - accordionRef.current?.getBoundingClientRect().bottom - 100

      if (dropdownListRef.current) {
        dropdownListRef.current.style.maxHeight = expectedHeight < 180 ? '180px' : expectedHeight + 'px'
        dropdownListRef.current.style.top = accordionRef.current?.offsetHeight
          ? accordionRef.current?.offsetHeight + 'px'
          : '40px'
      }
    }

    const throttledClick = throttle(handleClick, 200)

    if (sideDrawerComponent) {
      throttledClick()
      sideDrawerComponent.addEventListener('click', throttledClick)
    }

    return () => {
      if (sideDrawerComponent) sideDrawerComponent.removeEventListener('click', throttledClick)
    }
  }, [isDropdownOpen, dropdownListRef, accordionRef, selectedAudiences])

  const handleOnChange = (audiences) => {
    let updatedAudience = audiences.map((item) => item.id).filter((el) => el)
    updatedAudience = updatedAudience.length ? updatedAudience.join(',') : null
    onChange(updatedAudience)
  }

  // Handle TC audience selection (radio) - clears all other selections
  const handleSelectTC = () => {
    setSelectedAudiences([])
    handleOnChange([])
  }

  // Handle custom audience selection (checkbox) - clears TC and saved, allows multiple
  const handleSelectCustom = (audience) => {
    if (audience.id) {
      // Remove any saved audiences, keep only custom audiences
      const customOnly = selectedAudiences.filter((aud) => 
        options.some((opt) => opt.id === aud.id)
      )
      const newAudiences = [...customOnly, audience]
      setSelectedAudiences(newAudiences)
      handleOnChange(newAudiences)
      setDropdownOpen(true)
    }
  }

  // Handle saved audience selection (radio) - clears TC and custom, allows only one
  const handleSelectSaved = (audience) => {
    if (audience.id) {
      // Replace all selections with just this saved audience
      const newAudiences = [audience]
      setSelectedAudiences(newAudiences)
      handleOnChange(newAudiences)
      setDropdownOpen(true)
    }
  }

  const handleRemoveSelectedItem = (e, audience) => {
    e.stopPropagation()
    setSelectedAudiences((prevAudiences) => {
      let updatedAudience = audience.id ? prevAudiences.filter((prevAudience) => prevAudience.id !== audience.id) : []
      handleOnChange(updatedAudience)
      return updatedAudience
    })
  }

  useOnClickOutside(dropdownRef, () => {
    if (isDropdownOpen) {
      handleOnChange(selectedAudiences)
      setDropdownOpen(false)
    }
  })


  const tabsData = [
    {
      title: t('Recommendations.campaign.setup.audience.custom'),
      component: (
        <AudienceList
          options={options}
          placeholder={isMobile ? t('common.search') : t('Recommendations.campaign.setup.audience.placeholder.custom')}
          loading={loading.custom}
          error={error.custom}
          onErrorBtnClick={onErrorBtnClick}
          handleSelect={handleSelectCustom}
          handleRemoveSelectedItem={handleRemoveSelectedItem}
          selectedAudiences={selectedAudiences.filter((aud) => 
            options.some((opt) => opt.id === aud.id)
          )}
          inputType="checkbox"
        />
      ),
    },
    {
      title: t('Recommendations.campaign.setup.audience.saved'),
      component: (
        <AudienceList
          options={savedAudiences}
          placeholder={isMobile ? t('common.search') : t('Recommendations.campaign.setup.audience.placeholder.saved')}
          loading={loading.saved}
          error={error.saved}
          onErrorBtnClick={onErrorBtnClick}
          handleSelect={handleSelectSaved}
          handleRemoveSelectedItem={handleRemoveSelectedItem}
          selectedAudiences={selectedAudiences.filter((aud) => 
            savedAudiences.some((saved) => saved.id === aud.id)
          )}
          inputType="radio"
          radioName="saved-audience-radio"
        />
      ),
    },
  ]

  const optionsAvailable = options.length || error || loading

  return (
    <div className="search-dropdown" ref={dropdownRef}>
      <div className="accordion">
        <Card
          ref={accordionRef}
          className={classNames({
            disabled: disabled,
          })}
        >
          <Card.Header 
            as={Card.Header} 
            onClick={() => setDropdownOpen((prev) => !prev)} 
            className={classNames('text-left', {
              'has-selected-audience': selectedAudiences.length > 0
            })}
          >
            <SelectedAudiences
              defaultValue={defaultValue}
              audiences={selectedAudiences}
              handleRemoveSelectedItem={handleRemoveSelectedItem}
              disabled={disabled}
              isDropdownOpen={isDropdownOpen}
            />
          </Card.Header>
        </Card>
      </div>

      {isDropdownOpen && !disabled && (
        <div className={`search-dropdown_dropdown ${dropdownClasses}`} ref={dropdownListRef}>
          {optionsAvailable ? (
            <div>
              {defaultValue && (
                <div className="search-dropdown_dropdown_results">
                  <label htmlFor="audience-default-value" className="search-dropdown_dropdown_results_item">
                    <input
                      id="audience-default-value"
                      type="radio"
                      name="audience-type"
                      onChange={handleSelectTC}
                      checked={!selectedAudiences.length}
                    />
                    <div className="search-dropdown_dropdown_results_item_default">
                      <span className="search-dropdown_dropdown_results_item_recommended">{defaultValue}</span>

                      <i className="search-dropdown_dropdown_results_item_sub">
                        {t('Recommendations.campaign.setup.audience.recommendedSubtext')}
                      </i>
                    </div>
                  </label>

                  <hr className="divider" />
                </div>
              )}

              <Tabs tabsData={tabsData} currentTab={activeTab} handleTabChange={setActiveTab} />
            </div>
          ) : (
            <div className="select-ad-platform_select-menu_drop-menu_row_wrapper">
              <div style={{ color: '#5C5C5C', width: '100%', textAlign: 'center' }}>{t('common.noMoreFields')}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SearchDropdownMultiSelect.propTypes = {
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(object),
  onChange: PropTypes.func,
  onErrorBtnClick: PropTypes.func,
  error: PropTypes.shape({
    custom: PropTypes.string,
    saved: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  loading: PropTypes.shape({
    custom: PropTypes.bool,
    saved: PropTypes.bool,
  }),
  dropdownClasses: PropTypes.string,
}

export default SearchDropdownMultiSelect
